import React, { useMemo } from 'react';
import { ThemeProvider as StyledThemeProvider, createGlobalStyle } from 'styled-components';
import { ThemeAuto, ThemeLight, ThemeDark } from '../../styles';
import { HeadProvider } from '../../contexts/Head';
import { ConditionalWrapper } from '../../components/ConditionalWrapper';
import { ThemeProviderProps, ThemeGlobalStyleProps } from './ThemeProvider.types';

const GlobalStyle = createGlobalStyle<ThemeGlobalStyleProps>`
  :scope {
    ${({ vars }) => vars}
  }
`;

// Make tokens and themeType symbols to avoid conflicts with other themes
export const tokens: unique symbol = Symbol('tokens');
export const themeType: unique symbol = Symbol('type');

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children, theme = { type: 'auto' }, HeadComponent }) => {
  const { mergedTheme, cssVariables } = useMemo(() => {
    const { type = 'auto', ...customTheme } = theme;
    let themeObject;
    if (type === 'auto') {
      themeObject = ThemeAuto;
    } else if (type === 'light') {
      themeObject = ThemeLight;
    } else {
      themeObject = ThemeDark;
    }

    const { theme: themeTokens, cssVariables } = themeObject;

    const mergedTheme = {
      [tokens]: themeTokens,
      [themeType]: type,
      ...customTheme,
    };

    return {
      mergedTheme,
      cssVariables,
    };
  }, [theme]);

  return (
    <StyledThemeProvider theme={mergedTheme}>
      <ConditionalWrapper
        condition={!!HeadComponent}
        wrapper={(children) => <HeadProvider HeadComponent={HeadComponent!}>{children}</HeadProvider>}
      >
        <GlobalStyle vars={cssVariables} />
        {children}
      </ConditionalWrapper>
    </StyledThemeProvider>
  );
};
